import { useState } from 'react'
import { Patient } from '../types/patient'
import { Consultation, ConsultationCode, ConsultationTerminologySystem } from '../types/consultation'
import { TerminologySearchResult } from '../services/terminologyService'
import { PatientClinicalHistory } from './PatientClinicalHistory'
import { LabResultList } from './LabResultList'
import { ConsultationList } from './ConsultationList'
import { MedicationPrescriber } from './MedicationPrescriber'
import { PatientMedications } from './PatientMedications'
import { PrescriptionsTabContent } from './PrescriptionsTabContent'
import { ConsultationBillingItems } from './ConsultationBillingItems'
import './ConsultationWorkspaceTabs.css'

export type WorkspaceTab = 'clinical-history' | 'medications' | 'prescriptions' | 'lab-results' | 'past-consultations' | 'billing'

interface ConsultationWorkspaceTabsProps {
  patient: Patient | undefined
  consultation: Consultation
  allConsultations?: Consultation[]
  // Diagnosis-related props (kept for API compatibility but not used)
  selectedSystem: ConsultationTerminologySystem
  onSystemChange: (system: ConsultationTerminologySystem) => void
  codeSearch: string
  onCodeSearch: (query: string) => void
  codeResults: TerminologySearchResult[]
  isSearchingCodes: boolean
  onAddCode: (result: TerminologySearchResult) => void
  onRemoveCode: (index: number) => void
  diagnosis: string[]
  codes: ConsultationCode[]
  isCodeAddedAsCondition: (code: ConsultationCode) => string | null
  onAddAsCondition: (code: ConsultationCode, index: number) => void
  onRemoveAsCondition: (conditionId: string) => void
  addConditionMutationPending: boolean
  removeConditionMutationPending: boolean
}

export function ConsultationWorkspaceTabs({
  patient,
  consultation,
}: ConsultationWorkspaceTabsProps) {
  const [activeTab, setActiveTab] = useState<WorkspaceTab | null>(null)

  const tabs: Array<{ id: WorkspaceTab; label: string; icon: React.ReactNode; color: string }> = [
    { 
      id: 'clinical-history', 
      label: 'Clinical History', 
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
          <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
        </svg>
      ), 
      color: '#2563EB' 
    },
    { 
      id: 'medications', 
      label: 'Medications', 
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
          <line x1="9" y1="3" x2="9" y2="21"></line>
          <line x1="3" y1="9" x2="21" y2="9"></line>
          <circle cx="15" cy="15" r="2"></circle>
        </svg>
      ), 
      color: '#DC2626' 
    },
    { 
      id: 'prescriptions', 
      label: 'Prescriptions', 
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
          <polyline points="14 2 14 8 20 8"></polyline>
          <line x1="16" y1="13" x2="8" y2="13"></line>
          <line x1="16" y1="17" x2="8" y2="17"></line>
          <polyline points="10 9 9 9 8 9"></polyline>
        </svg>
      ), 
      color: '#059669' 
    },
    { 
      id: 'lab-results', 
      label: 'Lab Results', 
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="6" x2="12" y2="12"></line>
          <line x1="16" y1="10" x2="12" y2="12"></line>
        </svg>
      ), 
      color: '#7C3AED' 
    },
    { 
      id: 'past-consultations', 
      label: 'Past Consultations', 
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
          <line x1="16" y1="2" x2="16" y2="6"></line>
          <line x1="8" y1="2" x2="8" y2="6"></line>
          <line x1="3" y1="10" x2="21" y2="10"></line>
        </svg>
      ), 
      color: '#EA580C' 
    },
    { 
      id: 'billing', 
      label: 'Billing', 
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="4" width="18" height="16" rx="2" ry="2"></rect>
          <line x1="3" y1="10" x2="21" y2="10"></line>
          <line x1="8" y1="15" x2="10" y2="15"></line>
          <line x1="12" y1="15" x2="16" y2="15"></line>
        </svg>
      ), 
      color: '#0F766E' 
    },
  ]

  return (
    <div className="workspace-tabs-container">
      <div className="workspace-tabs-bar">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`workspace-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(activeTab === tab.id ? null : tab.id)}
            aria-label={tab.label}
            data-tab-color={tab.color}
            data-tab-id={tab.id}
          >
            <span className="tab-icon">{tab.icon}</span>
            <span className="tab-label">{tab.label}</span>
          </button>
        ))}
      </div>
      <div className={`workspace-tab-content ${activeTab !== null ? 'slide-in' : ''}`}>
        {activeTab === 'clinical-history' && patient && (
          <div className="tab-content-wrapper">
            <PatientClinicalHistory patientId={patient.id} />
          </div>
        )}
        {activeTab === 'medications' && patient && (
          <MedicationsTab patient={patient} />
        )}
        {activeTab === 'prescriptions' && patient && (
          <div className="tab-content-wrapper">
            <PrescriptionsTabContent patientId={patient.id} patient={patient} />
          </div>
        )}
        {activeTab === 'lab-results' && patient && (
          <div className="tab-content-wrapper">
            <LabResultList patientId={patient.id} navigateToDetail={true} />
          </div>
        )}
        {activeTab === 'past-consultations' && patient && (
          <div className="tab-content-wrapper">
            <ConsultationList patientId={patient.id} navigateToDetail={true} />
          </div>
        )}
        {activeTab === 'billing' && consultation && (
          <div className="tab-content-wrapper">
            <ConsultationBillingItems
              consultationId={consultation.id}
              patientId={consultation.patientId}
            />
          </div>
        )}
      </div>
    </div>
  )
}

// Medications Tab Component
interface MedicationsTabProps {
  patient: Patient
}

function MedicationsTab({ patient }: MedicationsTabProps) {
  return (
    <div className="tab-content-wrapper">
      <div className="medications-tab-content">
        {/* Current Medications List */}
        <div className="sidebar-section">
          <div className="section-header">
            <h3>Current Medications</h3>
          </div>
          <div className="medications-workspace-list">
            <PatientMedications patientId={patient.id} />
          </div>
        </div>

        {/* Prescribe medications panel, embedded within the Medications tab */}
        <div className="sidebar-section">
          <MedicationPrescriber patientId={patient.id} />
        </div>
      </div>
    </div>
  )
}
